// SPDX-License-Identifier: Apache-2.0
// File: src/ui/components/CustomChordModal.tsx
import React, { useState } from 'react';
import { PocketGridRetroPalette } from '../theme/PocketGridRetroTheme';
import {
  SmartChord,
  CHORD_ROOTS,
  ChordRootName,
  CHORD_CATEGORIES,
  CHORD_TYPE_DEFINITIONS,
  CHORD_PRESET_PACKS,
  buildSmartChord,
  getDefaultSmartChords,
} from '../../audio/ChordLibrary';

interface CustomChordModalProps {
  isOpen: boolean;
  onClose: () => void;
  chords: SmartChord[];
  onSaveChords: (chords: SmartChord[]) => void;
  onAuditionChord: (chord: SmartChord) => void;
}

export const CustomChordModal: React.FC<CustomChordModalProps> = ({
  isOpen,
  onClose,
  chords,
  onSaveChords,
  onAuditionChord,
}) => {
  const [editingChords, setEditingChords] = useState<SmartChord[]>(() => [...chords]);
  const [selectedSlotIdx, setSelectedSlotIdx] = useState<number>(0);
  const [activeCategory, setActiveCategory] = useState<string>('EXTENDED');

  if (!isOpen) return null;

  const currentChord = editingChords[selectedSlotIdx] || editingChords[0];

  const handleUpdateCurrentSlot = (
    root: ChordRootName,
    typeId: string,
    voicing: SmartChord['voicing']
  ) => {
    const updated = buildSmartChord(selectedSlotIdx, root, typeId, voicing);
    const nextList = [...editingChords];
    nextList[selectedSlotIdx] = updated;
    setEditingChords(nextList);
    onAuditionChord(updated);
  };

  const handleLoadPresetPack = (packId: string) => {
    const pack = CHORD_PRESET_PACKS.find((p) => p.id === packId);
    if (!pack) return;
    const nextList = pack.chords.map((c, idx) => buildSmartChord(idx, c.root, c.type, c.voicing));
    setEditingChords(nextList);
    if (nextList[selectedSlotIdx]) {
      onAuditionChord(nextList[selectedSlotIdx]);
    }
  };

  const handleApplyAndClose = () => {
    onSaveChords(editingChords);
    onClose();
  };

  const filteredChordTypes = CHORD_TYPE_DEFINITIONS.filter((d) => d.category === activeCategory);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(10, 11, 14, 0.94)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '860px',
          maxHeight: 'min(94dvh, calc(100vh - 12px))',
          height: '100%',
          background: '#151619',
          border: `1.5px solid ${PocketGridRetroPalette.champagneBrass}`,
          borderRadius: '8px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.95)',
          display: 'flex',
          flexDirection: 'column',
          color: PocketGridRetroPalette.warmIvoryText,
          overflow: 'hidden',
          boxSizing: 'border-box',
          fontFamily: '"Inter", -apple-system, system-ui, sans-serif',
        }}
      >
        {/* =====================================================================
            1. STICKY HEADER
           ===================================================================== */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 12px',
            borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            background: '#1C1D21',
            flexShrink: 0,
            minHeight: '36px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>🎹</span>
            <div>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 800,
                  color: PocketGridRetroPalette.champagneBrass,
                  letterSpacing: '0.04em',
                }}
              >
                SMART CHORD STUDIO • 10 CUSTOM SLOTS
              </div>
              <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText }}>
                Customize chord roots, qualities & voicings for live performance & sequencer step mapping
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={() => onAuditionChord(currentChord)}
              style={{
                height: '26px',
                padding: '0 10px',
                borderRadius: '4px',
                background: PocketGridRetroPalette.phosphorMoss,
                border: 'none',
                color: '#FFF',
                fontWeight: 800,
                fontSize: '10.5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <span>▶</span>
              <span>AUDITION {currentChord.label}</span>
            </button>

            <button
              onClick={onClose}
              style={{
                height: '26px',
                padding: '0 8px',
                borderRadius: '4px',
                background: '#282A2E',
                border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                color: PocketGridRetroPalette.warmIvoryText,
                fontWeight: 700,
                fontSize: '11px',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* =====================================================================
            2. TOP BAR: 8 TACTILE CHORD SLOT PADS (CLICK TO SELECT SLOT TO EDIT)
           ===================================================================== */}
        <div
          style={{
            padding: '6px 10px',
            background: '#18191D',
            borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              fontSize: '8.5px',
              fontWeight: 800,
              color: PocketGridRetroPalette.mutedPewterText,
              letterSpacing: '0.05em',
              marginBottom: '4px',
            }}
          >
            SELECT CHORD SLOT TO EDIT:
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: '4px',
            }}
          >
            {editingChords.map((chord, idx) => {
              const isSelected = selectedSlotIdx === idx;
              return (
                <button
                  key={idx}
                  onClick={() => {
                    setSelectedSlotIdx(idx);
                    onAuditionChord(chord);
                  }}
                  style={{
                    height: '38px',
                    borderRadius: '5px',
                    background: isSelected ? '#2D2316' : '#1C1D21',
                    border: `1.5px solid ${
                      isSelected
                        ? PocketGridRetroPalette.tungstenAmber
                        : PocketGridRetroPalette.panelBorder
                    }`,
                    color: isSelected
                      ? PocketGridRetroPalette.incandescentCream
                      : PocketGridRetroPalette.warmIvoryText,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: '2px',
                    boxShadow: isSelected ? '0 0 10px rgba(217, 148, 59, 0.45)' : 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span
                    style={{
                      fontSize: '7px',
                      fontWeight: 800,
                      opacity: 0.65,
                      lineHeight: 1,
                      color: isSelected
                        ? PocketGridRetroPalette.tungstenAmber
                        : PocketGridRetroPalette.mutedPewterText,
                    }}
                  >
                    SLOT {idx + 1}
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 900,
                      lineHeight: 1.1,
                      marginTop: '2px',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {chord.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* =====================================================================
            3. SCROLLABLE MIDDLE BODY: CHORD DESIGNER CONTROLS
           ===================================================================== */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '8px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {/* Section A: Quick Curated Chord Style Packs */}
          <div
            style={{
              background: '#1A1B1F',
              padding: '6px 10px',
              borderRadius: '6px',
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            }}
          >
            <div
              style={{
                fontSize: '9px',
                fontWeight: 800,
                color: PocketGridRetroPalette.champagneBrass,
                marginBottom: '4px',
                letterSpacing: '0.04em',
              }}
            >
              ⚡ QUICK-LOAD STYLE PACKS (LOADS 10 CURATED CHORDS):
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {CHORD_PRESET_PACKS.map((pack) => (
                <button
                  key={pack.id}
                  onClick={() => handleLoadPresetPack(pack.id)}
                  title={`${pack.description}`}
                  style={{
                    padding: '3px 8px',
                    borderRadius: '4px',
                    background: '#23252A',
                    border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                    color: PocketGridRetroPalette.warmIvoryText,
                    fontSize: '9.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <span>{pack.name}</span>
                  <span
                    style={{
                      fontSize: '7.5px',
                      padding: '1px 3px',
                      borderRadius: '2px',
                      background: '#161719',
                      color: PocketGridRetroPalette.tungstenAmber,
                    }}
                  >
                    {pack.badge}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Section B: Root Note Selector (12 Semitones) */}
          <div
            style={{
              background: '#1A1B1F',
              padding: '6px 10px',
              borderRadius: '6px',
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '4px',
              }}
            >
              <span
                style={{
                  fontSize: '9px',
                  fontWeight: 800,
                  color: PocketGridRetroPalette.tungstenAmber,
                  letterSpacing: '0.04em',
                }}
              >
                1. CHORD ROOT NOTE (SLOT {selectedSlotIdx + 1}):
              </span>
              <span style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText }}>
                Currently: <strong style={{ color: '#FFF' }}>{currentChord.root}</strong>
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '3px' }}>
              {CHORD_ROOTS.map((r) => {
                const isSelected = currentChord.root === r;
                return (
                  <button
                    key={r}
                    onClick={() =>
                      handleUpdateCurrentSlot(r, currentChord.type, currentChord.voicing)
                    }
                    style={{
                      height: '28px',
                      borderRadius: '4px',
                      background: isSelected ? PocketGridRetroPalette.tungstenAmber : '#23252A',
                      border: `1px solid ${
                        isSelected
                          ? PocketGridRetroPalette.incandescentCream
                          : PocketGridRetroPalette.panelBorder
                      }`,
                      color: isSelected ? '#141517' : PocketGridRetroPalette.warmIvoryText,
                      fontWeight: 800,
                      fontSize: '10.5px',
                      cursor: 'pointer',
                    }}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Section C: Chord Quality / Type Selector with Category Filter */}
          <div
            style={{
              background: '#1A1B1F',
              padding: '6px 10px',
              borderRadius: '6px',
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '6px',
                flexWrap: 'wrap',
                gap: '4px',
              }}
            >
              <span
                style={{
                  fontSize: '9px',
                  fontWeight: 800,
                  color: PocketGridRetroPalette.vintageVfdSage,
                  letterSpacing: '0.04em',
                }}
              >
                2. CHORD QUALITY & FAMILY:
              </span>

              {/* Category Pills */}
              <div style={{ display: 'flex', gap: '3px' }}>
                {CHORD_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    style={{
                      padding: '2px 6px',
                      borderRadius: '3px',
                      background: activeCategory === cat.id ? '#253528' : '#17181A',
                      border: `1px solid ${
                        activeCategory === cat.id
                          ? PocketGridRetroPalette.vintageVfdSage
                          : PocketGridRetroPalette.panelBorder
                      }`,
                      color:
                        activeCategory === cat.id
                          ? PocketGridRetroPalette.incandescentCream
                          : PocketGridRetroPalette.mutedPewterText,
                      fontSize: '8.5px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Grid of Chord Types in Active Category */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: '5px',
              }}
            >
              {filteredChordTypes.map((cType) => {
                const isSelected = currentChord.type === cType.id;
                return (
                  <button
                    key={cType.id}
                    onClick={() =>
                      handleUpdateCurrentSlot(
                        currentChord.root as ChordRootName,
                        cType.id,
                        currentChord.voicing
                      )
                    }
                    style={{
                      padding: '5px 8px',
                      borderRadius: '4px',
                      background: isSelected ? '#1E2D24' : '#222429',
                      border: `1px solid ${
                        isSelected
                          ? PocketGridRetroPalette.phosphorMoss
                          : PocketGridRetroPalette.panelBorder
                      }`,
                      color: isSelected
                        ? PocketGridRetroPalette.incandescentCream
                        : PocketGridRetroPalette.warmIvoryText,
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '1px',
                    }}
                  >
                    <div style={{ fontSize: '11px', fontWeight: 800 }}>
                      {currentChord.root}
                      {cType.shortLabel || ' Maj'}
                    </div>
                    <div
                      style={{
                        fontSize: '8px',
                        color: PocketGridRetroPalette.mutedPewterText,
                        lineHeight: 1.1,
                      }}
                    >
                      {cType.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Section D: Harmonic Voicing & Inversion Spreads */}
          <div
            style={{
              background: '#1A1B1F',
              padding: '6px 10px',
              borderRadius: '6px',
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            }}
          >
            <div
              style={{
                fontSize: '9px',
                fontWeight: 800,
                color: PocketGridRetroPalette.burntTerracotta,
                marginBottom: '4px',
                letterSpacing: '0.04em',
              }}
            >
              3. VOICING & ACOUSTIC SPREAD:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px' }}>
              {[
                { id: 'open' as const, label: 'OPEN SPREAD', desc: 'Root dropped 1 octave; wide, warm & airy' },
                { id: 'standard' as const, label: 'STANDARD ROOT', desc: 'Compact tight root position cluster' },
                { id: 'drop2' as const, label: 'DROP-2 JAZZ', desc: '2nd note from top dropped 1 oct; classic piano lead' },
                { id: 'quartal' as const, label: 'QUARTAL STACK', desc: 'Modern modal intervals stacked in 4ths' },
              ].map((v) => {
                const isSelected = currentChord.voicing === v.id;
                return (
                  <button
                    key={v.id}
                    onClick={() =>
                      handleUpdateCurrentSlot(
                        currentChord.root as ChordRootName,
                        currentChord.type,
                        v.id
                      )
                    }
                    style={{
                      padding: '5px 8px',
                      borderRadius: '4px',
                      background: isSelected ? '#331F18' : '#222429',
                      border: `1px solid ${
                        isSelected
                          ? PocketGridRetroPalette.burntTerracotta
                          : PocketGridRetroPalette.panelBorder
                      }`,
                      color: isSelected
                        ? PocketGridRetroPalette.incandescentCream
                        : PocketGridRetroPalette.warmIvoryText,
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: '10px', fontWeight: 800 }}>{v.label}</div>
                    <div
                      style={{
                        fontSize: '7.5px',
                        color: PocketGridRetroPalette.mutedPewterText,
                        lineHeight: 1.1,
                        marginTop: '2px',
                      }}
                    >
                      {v.desc}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* =====================================================================
            4. STICKY FOOTER
           ===================================================================== */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 12px',
            borderTop: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            background: '#1C1D21',
            flexShrink: 0,
            minHeight: '36px',
          }}
        >
          <button
            onClick={() => {
              const def = getDefaultSmartChords();
              setEditingChords(def);
              onAuditionChord(def[0]);
            }}
            style={{
              padding: '4px 10px',
              borderRadius: '4px',
              background: '#282A2E',
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
              color: PocketGridRetroPalette.mutedPewterText,
              fontWeight: 700,
              fontSize: '10px',
              cursor: 'pointer',
            }}
          >
            RESET DEFAULT PACK
          </button>

          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={handleApplyAndClose}
              style={{
                padding: '5px 16px',
                borderRadius: '4px',
                background: PocketGridRetroPalette.phosphorMoss,
                border: 'none',
                color: '#FFF',
                fontWeight: 800,
                fontSize: '11px',
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(78,154,103,0.4)',
              }}
            >
              ✓ APPLY 10 CHORDS
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
